import { ActionButton, ActionButtonProps } from '@if/mui'
import { Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// Extends ActionButtonProps (not raw ButtonProps), so `variant`/`color` aren't
// even accepted — the look is the design-system default button (tone/emphasis),
// with no per-call overrides. `startIcon` is a lucide component the button sizes.
interface CsvExportButtonProps extends Omit<ActionButtonProps, 'onClick'> {
  filename: string
  headers?: (string | number)[]
  rows: (string | number)[][]
}

const toCsvValue = (value: string | number) => {
  const normalized = String(value)
    .replace(/\r?\n/g, ' ')
    .replace(/\u00a0/g, ' ')
    .trim()
  const needsEscaping = normalized.includes(',') || normalized.includes('"')
  if (!needsEscaping) {
    return normalized
  }
  const escaped = normalized.replace(/"/g, '""')
  return `"${escaped}`.concat('"')
}

export const CsvExportButton = ({
  filename,
  headers = [],
  rows,
  disabled,
  startIcon,
  children,
  ...buttonProps
}: CsvExportButtonProps) => {
  const { t } = useTranslation()
  const handleExport = () => {
    if (!headers.length && !rows.length) {
      return
    }

    const csvRows = headers.length ? [headers, ...rows] : rows
    const csvContent = csvRows.map((row) => row.map(toCsvValue).join(',')).join('\r\n')

    const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <ActionButton {...buttonProps} startIcon={startIcon ?? Download} onClick={handleExport} disabled={disabled}>
      {t('common.exportCsv')}
      {children}
    </ActionButton>
  )
}
